import { inject, Injectable } from '@angular/core'

import { MessageService } from 'primeng/api'
import { firstValueFrom, Subject } from 'rxjs'

import { CountryCodes, FleetForm, FleetView } from '@ti/contracts'
import { ApiService } from '@ti/core/api'
import { DIALOG_FACADE } from '@ti/core/contracts/dialog'
import { DateFormatService, getSettingsByCountryCode, LanguageService } from '@ti/core/ui-kit/i18n'

import { PricingPlansProvider } from './pricing-plans.provider'

@Injectable({ providedIn: 'root' })
export class FleetAccountsManager {
  public readonly events = {
    updated$: new Subject<number[] | void>(),
  }

  protected readonly api = inject(ApiService)
  protected readonly pricingPlansProvider = inject(PricingPlansProvider)
  protected readonly dateFormatService = inject(DateFormatService)
  protected readonly dialogFacade = inject(DIALOG_FACADE)
  protected readonly languageService = inject(LanguageService)
  protected readonly messageService = inject(MessageService)

  public async launchFleet(data: FleetForm) {
    await this.api.tsp.fleet.launch(this.prepareFleetData(data))
    this.events.updated$.next()
    this.messageService.add({
      summary: await this.languageService.translate(
        'tsp-admin.fleets.notifications.launched-title',
      ),
      detail:
        // Do not show an `invited` message in the notification
        !data.email || (data.username && data.password)
          ? ''
          : await this.languageService.translate(
              'tsp-admin.fleets.notifications.launched-summary',
              { email: data.email },
            ),
      life: 5000,
      severity: 'success',
    })
  }

  public async updateFleet(fleet: FleetView, data: FleetForm) {
    let isApproved: boolean = true
    if (fleet.pricingPlanId > data.pricingPlanId) {
      const pricingPlanLabels = await firstValueFrom(this.pricingPlansProvider.pricingPlansLabels$)
      const labels = await this.languageService.massTranslate(
        {
          summary: 'tsp-admin.fleets.downgrade-dialog.summary',
          description: 'tsp-admin.fleets.downgrade-dialog.description',
          agreeDescription: 'tsp-admin.fleets.downgrade-dialog.agree-description',
          confirmButton: 'tsp-admin.fleets.downgrade-dialog.confirm',
          backButton: 'tsp-admin.fleets.downgrade-dialog.back-button',
          cancelButton: 'button.cancel',
        },
        {
          accountName: fleet.name,
          planFrom: pricingPlanLabels[fleet.pricingPlanId],
          planTo: pricingPlanLabels[data.pricingPlanId],
        },
      )
      const dialogResult = await this.dialogFacade.confirmWithAgree({
        ...labels,
        confirmButtonSeverity: 'danger',
      })

      isApproved = dialogResult === 'confirm'
    }

    if (isApproved) {
      const updatedFleet = await this.api.tsp.fleet.update(fleet.id, this.prepareFleetData(data))
      this.events.updated$.next([updatedFleet.id])
      this.messageService.add({
        summary: await this.languageService.translate('tsp-admin.fleets.notifications.updated', {
          name: data.name,
        }),
        life: 5000,
        severity: 'success',
      })
      return true
    }
    return false
  }

  public async activateFleet(fleet: FleetView) {
    try {
      await this.api.tsp.fleet.activate(fleet.id)
      this.events.updated$.next([fleet.id])
      this.messageService.add({
        summary: await this.languageService.translate('tsp-admin.fleets.notifications.resumed', {
          name: fleet.name,
        }),
        life: 5000,
        severity: 'success',
      })
      return true
    } catch (error) {
      console.warn(`Cannot activate fleet`, error)
      await this.showUnknownErrorNotification()
    }
    return false
  }

  public async deleteFleet(fleet: FleetView) {
    const labels = await this.getDeleteDialogLabels(fleet.name)
    const dialogResult = await this.dialogFacade.confirm({
      ...labels,
      confirmButtonColor: 'var(--color-alert-500)',
      confirmButtonIcon: 'pi-trash',
    })
    if (dialogResult) {
      try {
        await this.api.tsp.fleet.deleteFleet(fleet.id)
        this.events.updated$.next()
        this.messageService.add({
          summary: await this.languageService.translate('tsp-admin.fleets.notifications.deleted', {
            name: fleet.name,
          }),
          life: 5000,
          severity: 'success',
        })
        return true
      } catch (error) {
        console.warn(`Cannot delete fleet`, error)
        await this.showUnknownErrorNotification()
      }
    }
    return false
  }

  public async suspendFleet(fleet: FleetView) {
    const labels = await this.getSuspendDialogLabels(fleet.name)
    const dialogResult = await this.dialogFacade.confirm({
      ...labels,
      confirmButtonColor: 'var(--color-alert-500)',
      confirmButtonIcon: 'pi-trash',
    })
    if (dialogResult) {
      try {
        await this.api.tsp.fleet.deactivate(fleet.id)
        this.events.updated$.next()
        this.messageService.add({
          summary: await this.languageService.translate(
            'tsp-admin.fleets.notifications.suspended',
            { name: fleet.name },
          ),
          life: 5000,
          severity: 'success',
        })
        return true
      } catch (error) {
        console.warn(`Cannot suspend fleet`, error)
        await this.showUnknownErrorNotification()
      }
    }
    return false
  }

  protected async getDeleteDialogLabels(name: string) {
    const [summary, description, confirmationMessage, confirmButton, cancelButton] =
      await Promise.all([
        this.languageService.translate('tsp-admin.fleets.delete-dialog.summary', { name }),
        this.languageService.translate('tsp-admin.fleets.delete-dialog.description'),
        this.languageService.translate('tsp-admin.fleets.delete-dialog.delete-confirm-text'),
        this.languageService.translate('tsp-admin.fleets.delete-dialog.confirm-button'),
        this.languageService.translate('tsp-admin.fleets.delete-dialog.cancel-button'),
      ])
    return {
      summary,
      description,
      confirmationMessage,
      confirmButton,
      cancelButton,
    }
  }

  protected async getSuspendDialogLabels(name: string) {
    const [summary, description, confirmButton, cancelButton] = await Promise.all([
      this.languageService.translate('tsp-admin.fleets.suspend-dialog.summary', { name }),
      this.languageService.translate('tsp-admin.fleets.suspend-dialog.description'),
      this.languageService.translate('tsp-admin.fleets.suspend-dialog.confirm-button'),
      this.languageService.translate('tsp-admin.fleets.suspend-dialog.cancel-button'),
    ])
    return { summary, description, confirmButton, cancelButton }
  }

  protected async showUnknownErrorNotification() {
    this.messageService.add({
      summary: await this.languageService.translate('tsp-admin.fleets.errors.unknown'),
      severity: 'error',
      life: 5000,
    })
  }

  protected prepareFleetData(data: FleetForm) {
    return {
      name: data.name,
      email: data.email,
      phone: data.phone,
      username: data.username,
      password: data.password,
      contactName: data.contactName,
      country: data.country,
      timezone: data.timezone,
      language: getSettingsByCountryCode(data.country as CountryCodes).lang,
      address: data.address,
      city: data.city,
      region: data.region,
      zipCode: data.zipCode,
      measurementSystem: {
        distance: data.distance,
        volume: data.volume,
        consumption: data.consumption,
        temperature: data.temperature,
      },
      pricingPlanId: data.pricingPlanId,
      templateId: data.templateId || undefined,
      isDemo: data.isDemo,
      enableExperimentalFeatures: data.enableExperimentalFeatures,
    }
  }
}
